import React, { useState, useEffect } from 'react'
import axios from 'axios'
import {
  BarChart, Bar, LineChart, Line,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer, Cell,
} from 'recharts'

const API  = process.env.REACT_APP_API_URL || 'http://localhost:8000'
const FONT = "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif"
const TEAL      = '#0d9488'
const TEAL_DARK = '#0f766e'
const TEAL_LIGHT = '#f0fdfa'
const PURPLE     = '#7c3aed'
const PURPLE_DARK = '#6d28d9'
const SLATE      = '#64748b'

function StatCard({ label, value, sub, color }) {
  return (
    <div style={{
      background: 'rgba(255,255,255,0.92)', borderRadius: '14px', padding: '18px 20px',
      position: 'relative', overflow: 'hidden',
    }}>
      <div style={{
        position: 'absolute', inset: 0, borderRadius: '14px', padding: '1.5px',
        background: `linear-gradient(135deg, ${TEAL}, ${PURPLE})`,
        WebkitMask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
        WebkitMaskComposite: 'xor', maskComposite: 'exclude', pointerEvents: 'none',
      }} />
      <div style={{ fontSize: '11px', fontWeight: '700', color: SLATE, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '6px' }}>{label}</div>
      <div style={{ fontSize: '28px', fontWeight: '700', color: color || TEAL_DARK, letterSpacing: '-0.02em', lineHeight: 1.1 }}>{value}</div>
      {sub && <div style={{ fontSize: '12px', color: SLATE, marginTop: '4px' }}>{sub}</div>}
    </div>
  )
}

function SectionHead({ title, sub }) {
  return (
    <div style={{ marginBottom: '16px' }}>
      <h2 style={{ fontSize: '16px', fontWeight: '700', color: '#0f172a', margin: 0, letterSpacing: '-0.01em' }}>{title}</h2>
      {sub && <p style={{ fontSize: '13px', color: SLATE, margin: '3px 0 0' }}>{sub}</p>}
    </div>
  )
}

function ChartCard({ children, style }) {
  return (
    <div style={{
      background: 'rgba(255,255,255,0.92)', borderRadius: '16px', padding: '20px 20px 12px',
      position: 'relative', overflow: 'hidden', ...style,
    }}>
      <div style={{
        position: 'absolute', inset: 0, borderRadius: '16px', padding: '1.5px',
        background: `linear-gradient(135deg, ${TEAL}, ${PURPLE})`,
        WebkitMask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
        WebkitMaskComposite: 'xor', maskComposite: 'exclude', pointerEvents: 'none',
      }} />
      {children}
    </div>
  )
}

const INJURY_COLORS = ['#0d9488','#7c3aed','#f59e0b','#ef4444','#3b82f6','#10b981','#f97316','#8b5cf6']

export default function Analytics({ clinicId, isDemo }) {
  const [data, setData]       = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError]     = useState(null)

  useEffect(() => {
    setLoading(true)
    setError(null)
    axios.get(API + '/analytics' + (isDemo ? '?demo=true' : ''))
      .then(res => { setData(res.data); setLoading(false) })
      .catch(err => { setError(err.response?.data?.detail || 'Failed to load analytics'); setLoading(false) })
  }, [clinicId, isDemo])

  if (loading) return (
    <div style={{ fontFamily: FONT, display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '60vh' }}>
      <div style={{ textAlign: 'center', color: SLATE }}>
        <div style={{ width: 36, height: 36, borderRadius: '50%', border: '3px solid #e2e8f0', borderTop: `3px solid ${TEAL}`, margin: '0 auto 14px', animation: 'spin .7s linear infinite' }} />
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        Loading analytics…
      </div>
    </div>
  )

  if (error) return (
    <div style={{ fontFamily: FONT, padding: '40px', textAlign: 'center', color: '#ef4444' }}>
      <div style={{ fontSize: 32, marginBottom: 12 }}>⚠️</div>
      <div style={{ fontSize: 15, fontWeight: 600 }}>{error}</div>
    </div>
  )

  const {
    visits_by_week = [],
    outcomes_by_injury = [],
    appointment_stats = {},
    no_show_rate = 0,
    top_referrers = [],
    revenue_by_month = [],
    avg_weekly_visits = 0,
  } = data || {}

  const totalRevenue = revenue_by_month.reduce((s, r) => s + r.paid, 0)
  const totalBilled  = revenue_by_month.reduce((s, r) => s + r.billed, 0)
  const collectionRate = totalBilled > 0 ? Math.round((totalRevenue / totalBilled) * 100) : 0

  return (
    <div style={{ fontFamily: FONT }}>
      {/* Header */}
      <div style={{ marginBottom: '24px' }}>
        <h1 style={{ fontSize: '26px', fontWeight: '700', color: '#0f172a', margin: 0, letterSpacing: '-0.02em' }}>Analytics</h1>
        <p style={{ fontSize: '14px', color: SLATE, margin: '4px 0 0' }}>Clinic-wide performance — last 12 weeks</p>
      </div>

      {/* ── Quick stats row ── */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '12px', marginBottom: '28px' }}>
        <StatCard label="Avg visits / week" value={avg_weekly_visits} sub="last 12 weeks" />
        <StatCard label="No-show / cancel rate" value={no_show_rate + '%'} sub={`${appointment_stats.cancelled || 0} of ${appointment_stats.total || 0} appts`} color={no_show_rate > 20 ? '#ef4444' : TEAL_DARK} />
        <StatCard label="Total collected" value={'$' + totalRevenue.toLocaleString()} sub="last 6 months" color={PURPLE_DARK} />
        <StatCard label="Collection rate" value={collectionRate + '%'} sub={`$${totalBilled.toLocaleString()} billed`} color={collectionRate < 80 ? '#f59e0b' : TEAL_DARK} />
      </div>

      {/* ── Visits per week + avg pain ── */}
      <ChartCard style={{ marginBottom: '20px' }}>
        <SectionHead title="Visits per Week" sub="Session volume with average pain score overlay" />
        {visits_by_week.length === 0 ? (
          <div style={{ height: 220, display: 'flex', alignItems: 'center', justifyContent: 'center', color: SLATE, fontSize: 14 }}>No session data yet — log sessions to see trends</div>
        ) : (
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={visits_by_week} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
              <XAxis dataKey="week" tick={{ fontSize: 11, fill: SLATE }} />
              <YAxis yAxisId="left" tick={{ fontSize: 11, fill: SLATE }} allowDecimals={false} />
              <YAxis yAxisId="right" orientation="right" domain={[0, 10]} tick={{ fontSize: 11, fill: '#f59e0b' }} />
              <Tooltip
                contentStyle={{ borderRadius: 10, border: `1px solid #e2e8f0`, fontFamily: FONT, fontSize: 13 }}
                formatter={(val, name) => [name === 'Avg Pain' ? val + '/10' : val, name]}
              />
              <Legend wrapperStyle={{ fontSize: 12, paddingTop: 8 }} />
              <Bar yAxisId="left" dataKey="visits" name="Visits" fill={TEAL} radius={[4, 4, 0, 0]} />
              <Line yAxisId="right" type="monotone" dataKey="avg_pain" name="Avg Pain" stroke="#f59e0b" strokeWidth={2} dot={{ r: 3 }} />
            </BarChart>
          </ResponsiveContainer>
        )}
      </ChartCard>

      {/* ── Outcomes by injury type ── */}
      <ChartCard style={{ marginBottom: '20px' }}>
        <SectionHead title="Outcome Scores by Injury Type" sub="Average intake pain vs. most recent pain score (lower is better)" />
        {outcomes_by_injury.length === 0 ? (
          <div style={{ height: 220, display: 'flex', alignItems: 'center', justifyContent: 'center', color: SLATE, fontSize: 14 }}>Log sessions with pain scores to see outcomes</div>
        ) : (
          <ResponsiveContainer width="100%" height={Math.max(220, outcomes_by_injury.length * 52)}>
            <BarChart layout="vertical" data={outcomes_by_injury} margin={{ top: 5, right: 20, left: 120, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" horizontal={false} />
              <XAxis type="number" domain={[0, 10]} tick={{ fontSize: 11, fill: SLATE }} />
              <YAxis type="category" dataKey="injury" tick={{ fontSize: 12, fill: '#374151' }} width={120} />
              <Tooltip
                contentStyle={{ borderRadius: 10, border: '1px solid #e2e8f0', fontFamily: FONT, fontSize: 13 }}
                formatter={(val, name) => [val + '/10', name]}
              />
              <Legend wrapperStyle={{ fontSize: 12, paddingTop: 8 }} />
              <Bar dataKey="intake_pain" name="Intake Pain" fill="#f87171" radius={[0, 4, 4, 0]} />
              <Bar dataKey="current_pain" name="Current Pain" fill={TEAL} radius={[0, 4, 4, 0]} />
            </BarChart>
          </ResponsiveContainer>
        )}
      </ChartCard>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '20px' }}>

        {/* ── Appointment breakdown ── */}
        <ChartCard>
          <SectionHead title="Appointment Status" sub="All-time counts" />
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginTop: '4px' }}>
            {[
              { label: 'Confirmed', key: 'confirmed', color: TEAL },
              { label: 'Requested / Pending', key: 'requested', color: '#f59e0b' },
              { label: 'Cancelled / No-show', key: 'cancelled', color: '#ef4444' },
            ].map(({ label, key, color }) => {
              const count = appointment_stats[key] || 0
              const total = appointment_stats.total || 1
              const pct   = Math.round((count / total) * 100)
              return (
                <div key={key}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '5px' }}>
                    <span style={{ fontSize: '13px', fontWeight: '600', color: '#374151' }}>{label}</span>
                    <span style={{ fontSize: '13px', color: SLATE }}>{count} ({pct}%)</span>
                  </div>
                  <div style={{ height: '7px', background: '#f1f5f9', borderRadius: '99px', overflow: 'hidden' }}>
                    <div style={{ height: '100%', width: pct + '%', background: color, borderRadius: '99px', transition: 'width .4s ease' }} />
                  </div>
                </div>
              )
            })}
            <div style={{ marginTop: '8px', fontSize: '12px', color: SLATE }}>
              No-show / cancel rate: <strong style={{ color: no_show_rate > 20 ? '#ef4444' : TEAL_DARK }}>{no_show_rate}%</strong>
            </div>
          </div>
        </ChartCard>

        {/* ── Top referring doctors ── */}
        <ChartCard>
          <SectionHead title="Top Referring Doctors" sub="By patient count" />
          {top_referrers.length === 0 ? (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 120, color: SLATE, fontSize: 14 }}>No referral data yet</div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginTop: '4px' }}>
              {top_referrers.slice(0, 6).map((r, i) => {
                const maxPts = top_referrers[0].patients
                const pct    = Math.round((r.patients / maxPts) * 100)
                return (
                  <div key={i}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                      <div>
                        <span style={{ fontSize: '13px', fontWeight: '600', color: '#374151' }}>{r.doctor}</span>
                        {r.practice && <span style={{ fontSize: '11px', color: SLATE, marginLeft: 6 }}>{r.practice}</span>}
                      </div>
                      <span style={{ fontSize: '13px', color: SLATE, flexShrink: 0, marginLeft: 8 }}>{r.patients} pt{r.patients !== 1 ? 's' : ''}</span>
                    </div>
                    <div style={{ height: '6px', background: '#f1f5f9', borderRadius: '99px', overflow: 'hidden' }}>
                      <div style={{ height: '100%', width: pct + '%', background: INJURY_COLORS[i % INJURY_COLORS.length], borderRadius: '99px', transition: 'width .4s ease' }} />
                    </div>
                  </div>
                )
              })}
            </div>
          )}
        </ChartCard>
      </div>

      {/* ── Revenue by month ── */}
      {revenue_by_month.length > 0 && (
        <ChartCard>
          <SectionHead title="Revenue — Last 6 Months" sub="Collected vs. billed" />
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={revenue_by_month} margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
              <XAxis dataKey="month" tick={{ fontSize: 11, fill: SLATE }} />
              <YAxis tick={{ fontSize: 11, fill: SLATE }} tickFormatter={v => '$' + (v >= 1000 ? (v / 1000).toFixed(1) + 'k' : v)} />
              <Tooltip
                contentStyle={{ borderRadius: 10, border: '1px solid #e2e8f0', fontFamily: FONT, fontSize: 13 }}
                formatter={(val) => ['$' + val.toLocaleString()]}
              />
              <Legend wrapperStyle={{ fontSize: 12, paddingTop: 8 }} />
              <Bar dataKey="billed" name="Billed" fill="#e2e8f0" radius={[4, 4, 0, 0]} />
              <Bar dataKey="paid" name="Collected" fill={PURPLE} radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
      )}

      {revenue_by_month.length === 0 && (
        <ChartCard>
          <SectionHead title="Revenue" sub="No invoice data yet" />
          <div style={{ height: 100, display: 'flex', alignItems: 'center', justifyContent: 'center', color: SLATE, fontSize: 14 }}>
            Create patient invoices in the Billing tab to track revenue here.
          </div>
        </ChartCard>
      )}
    </div>
  )
}
